import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { websiteSettingsAPI } from '../../services/api';

const Header = styled.header`
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.md};
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
`;

const LogoIcon = styled.div`
  width: 70px;
  height: 70px;
  min-width: 70px;
  min-height: 70px;
  background-color: ${props => props.hasLogo ? 'transparent' : props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.white};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
  
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: ${props => props.theme.typography.fontSize['2xl']};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
  }
`;

const LogoText = styled.div`
  h1 {
    font-size: ${props => props.theme.typography.fontSize.xl};
    font-weight: ${props => props.theme.typography.fontWeight.bold};
    color: ${props => props.theme.colors.primary};
    margin: 0;
    font-family: ${props => props.theme.typography.fontFamily.heading};
  }
  
  p {
    font-size: ${props => props.theme.typography.fontSize.sm};
    color: ${props => props.theme.colors.text.secondary};
    margin: 0;
    font-family: ${props => props.theme.typography.fontFamily.primary};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: ${props => props.mobileMenuOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: ${props => props.theme.colors.white};
    flex-direction: column;
    padding: 1rem;
    box-shadow: ${props => props.theme.shadows.lg};
    gap: 0;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    gap: 0;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.active ? props.theme.typography.fontWeight.semibold : props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  cursor: pointer;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: left;
    padding: 1rem;
    border-bottom: 1px solid ${props => props.theme.colors.border.primary};
  }
`;

const NavGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

const SubMenuContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isVisible ? '0' : '-10px'});
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
  
  @media (max-width: 768px) {
    position: static;
    opacity: 1;
    visibility: visible;
    transform: none;
    pointer-events: auto;
  }
`;

const GroupButton = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const SubMenu = styled.div`
  min-width: 180px;
  background-color: ${props => props.theme.colors.white};
  box-shadow: ${props => props.theme.shadows.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: 0.5rem;
  padding: 0.25rem 0;
  z-index: 1100;
  
  @media (max-width: 768px) {
    position: static;
    box-shadow: none;
    border-radius: 0;
    margin-top: 0;
    padding: 0;
    border-left: 2px solid ${props => props.theme.colors.border.primary};
  }
`;

const SubMenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  padding: 0.5rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  color: ${props => props.theme.colors.text.primary};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.theme.colors.gray[100]};
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    padding-left: 1.5rem;
  }
`;

const LoginButton = styled.button`
  padding: 0.5rem 1.5rem;
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.primaryDark || props.theme.colors.primary};
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 1rem;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const PublicHeader = ({ theme }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [websiteSettings, setWebsiteSettings] = useState(null);
  const [openGroup, setOpenGroup] = useState(null); // 'announcement' | 'membership' | null
  const [hoveredGroup, setHoveredGroup] = useState(null); // For desktop hover

  useEffect(() => {
    fetchWebsiteSettings();
  }, []);

  const fetchWebsiteSettings = async () => {
    try {
      const response = await websiteSettingsAPI.getSettings();
      console.log('Header - Website settings response:', response);
      if (response.data) {
        console.log('Header - Setting website settings:', response.data);
        console.log('Header - Logo path:', response.data.logo_path);
        console.log('Header - Website name:', response.data.website_name);
        setWebsiteSettings(response.data);
      } else {
        console.log('Header - No website settings found');
      }
    } catch (error) {
      console.error('Error fetching website settings:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleNavigation = (path) => {
    // Handle hash links for landing page sections
    if (path.startsWith('#')) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.querySelector(path);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(path);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(path);
    }
    setMobileMenuOpen(false);
    setOpenGroup(null);
  };

  // Debug: Log current state
  useEffect(() => {
    if (websiteSettings) {
      console.log('Header - Current websiteSettings state:', websiteSettings);
      console.log('Header - Logo path:', websiteSettings.logo_path);
      console.log('Header - Website name:', websiteSettings.website_name);
    }
  }, [websiteSettings]);

  return (
    <Header theme={theme}>
      <HeaderContent>
        <Logo onClick={() => handleNavigation('/')}>
          <LogoIcon 
            theme={theme} 
            hasLogo={!!websiteSettings?.logo_path}
          >
            {websiteSettings?.logo_path ? (
              <img 
                src={websiteSettings.logo_path} 
                alt={websiteSettings.website_name || "EWHRDN Logo"}
                onError={(e) => {
                  console.error('Header logo image failed to load:', websiteSettings.logo_path);
                  console.error('Failed image src:', e.target.src);
                  e.target.style.display = 'none';
                }}
                onLoad={() => {
                  console.log('Header logo loaded successfully:', websiteSettings.logo_path);
                }}
              />
            ) : (
              <span>E</span>
            )}
          </LogoIcon>
          <LogoText theme={theme}>
            <h1>{websiteSettings?.website_name || 'EWHRDN'}</h1>
            <p>Ethiopian Women Human Rights Defenders Network</p>
          </LogoText>
        </Logo>
        
        <Navigation mobileMenuOpen={mobileMenuOpen} theme={theme}>
          <NavLinks>
            <NavLink 
              theme={theme}
              active={isActive('/')}
              onClick={() => handleNavigation('/')}
            >
              Home
            </NavLink>
            <NavLink 
              theme={theme}
              active={isActive('/about')}
              onClick={() => handleNavigation('/about')}
            >
              About
            </NavLink>
            
            {/* Announcement Section: News, Events, Gallery */}
            <NavGroup
              onMouseEnter={() => setHoveredGroup('announcement')}
              onMouseLeave={() => setHoveredGroup(null)}
            >
              <GroupButton
                theme={theme}
                active={
                  isActive('/news') ||
                  isActive('/events') ||
                  isActive('/gallery')
                }
                onClick={() =>
                  setOpenGroup(prev =>
                    prev === 'announcement' ? null : 'announcement'
                  )
                }
              >
                Announcements
              </GroupButton>
              <SubMenuContainer 
                isVisible={hoveredGroup === 'announcement' || openGroup === 'announcement'}
              >
                <SubMenu theme={theme}>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/news')}
                  >
                    News
                  </SubMenuItem>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/events')}
                  >
                    Events
                  </SubMenuItem>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/gallery')}
                  >
                    Gallery
                  </SubMenuItem>
                </SubMenu>
              </SubMenuContainer>
            </NavGroup>

            <NavLink 
              theme={theme}
              active={isActive('/publications')}
              onClick={() => handleNavigation('/publications')}
            >
              Publications
            </NavLink>

            <NavLink 
              theme={theme}
              active={isActive('/surveys')}
              onClick={() => handleNavigation('/surveys')}
            >
              Survey
            </NavLink>
            <NavLink 
              theme={theme}
              active={isActive('/jobs')}
              onClick={() => handleNavigation('/jobs')}
            >
              Jobs
            </NavLink>
            
            {/* Membership Section: application, membership, status */}
            <NavGroup
              onMouseEnter={() => setHoveredGroup('membership')}
              onMouseLeave={() => setHoveredGroup(null)}
            >
              <GroupButton
                theme={theme}
                active={
                  isActive('/membership-applications') ||
                  isActive('/application-status')
                }
                onClick={() =>
                  setOpenGroup(prev =>
                    prev === 'membership' ? null : 'membership'
                  )
                }
              >
                Membership
              </GroupButton>
              <SubMenuContainer 
                isVisible={hoveredGroup === 'membership' || openGroup === 'membership'}
              >
                <SubMenu theme={theme}>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/membership-applications')}
                  >
                    Membership Application
                  </SubMenuItem>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/membership-applications')}
                  >
                    Membership Forms
                  </SubMenuItem>
                  <SubMenuItem
                    theme={theme}
                    onClick={() => handleNavigation('/application-status')}
                  >
                    Application Status
                  </SubMenuItem>
                </SubMenu>
              </SubMenuContainer>
            </NavGroup>
            <NavLink 
              theme={theme}
              active={isActive('/donate')}
              onClick={() => handleNavigation('/donate')}
            >
              Donate
            </NavLink>
            <NavLink 
              theme={theme}
              active={isActive('/contact')}
              onClick={() => handleNavigation('/contact')}
            >
              Contact
            </NavLink>
            <LoginButton 
              theme={theme}
              onClick={() => handleNavigation('/login')}
            >
              Login
            </LoginButton>
          </NavLinks>
        </Navigation>
        
        <MobileMenuButton 
          theme={theme}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>
      </HeaderContent>
    </Header>
  );
};

export default PublicHeader;

